import { useEffect, useState } from 'react'
import { useGLTF } from '@react-three/drei'
import type { ObjectMap } from '@react-three/fiber'
import type { GLTF } from 'three-stdlib'

/**
 * Module-level refcount of how many mounted consumers reference each GLTF url.
 * drei's `useGLTF` caches the parsed GLTF globally; if every consumer of a url
 * unmounts we want to release that cache entry (and its GPU resources) by
 * calling `useGLTF.clear(url)`. But clearing while another consumer is still
 * mounted would corrupt the shared scene/geometry, so we only clear when the
 * refcount for that url hits zero.
 */
const refCounts = new Map<string, number>()

/**
 * Pending deferred clears, keyed by url. The teardown is deferred (not run
 * synchronously in the effect cleanup) so a near-immediate re-mount can cancel
 * it before any GPU resources are disposed — see the cleanup below.
 */
const pendingClears = new Map<string, ReturnType<typeof setTimeout>>()

/**
 * The url each object path is currently loaded (held) under, keyed by
 * `stableKey`. Set when the first consumer subscribes; dropped when the
 * deferred clear actually releases the cache entry. A consumer that mounts
 * with a re-signed url while another still holds the model adopts the held
 * url, so it hits the same drei cache entry instead of downloading again. Once
 * nothing holds it, the next consumer loads from its own (fresh) url — never
 * from a signature that may have expired.
 */
const heldUrls = new Map<string, string>()

/** How long to wait after the last consumer unmounts before releasing the
 * cache entry. Long enough to bridge a StrictMode mount→unmount→mount cycle and
 * fast scroll-away/scroll-back, short enough to bound GPU memory. */
const CLEAR_DELAY_MS = 1000

/**
 * Presigned S3 urls carry a volatile query string (`Signature`, `Expires`,
 * `x-amz-security-token`) that changes every time the backend re-signs — e.g.
 * on each `/status` refetch. The bytes the url points at are immutable, so the
 * object path (everything before `?`) is the stable identity. Keying the loader
 * on the path prevents a re-signed url from being treated as a brand-new model
 * and reloaded (which manifested as the preview flashing and re-fetching the
 * GLB repeatedly).
 */
function stableKey(url: string): string {
  const q = url.indexOf('?')
  return q === -1 ? url : url.slice(0, q)
}

/**
 * Wraps drei's `useGLTF(url)` with two robustness guarantees:
 *
 * 1. **Stable identity** — re-signed presigned urls (same object path, new
 *    query string) resolve to the *first* url we loaded, so the model is never
 *    reloaded just because its signature changed.
 * 2. **Safe, refcounted release** — the underlying cache entry is released
 *    (`useGLTF.clear`) only once the last consumer for that url unmounts, and
 *    even then only after a short delay so a StrictMode remount (or a quick
 *    scroll-away/scroll-back) cancels the release before any GPU resource the
 *    still-mounted consumer's clone depends on is disposed.
 */
export function useDisposableGLTF(url: string): GLTF & ObjectMap {
  // Adopt a new url only when the underlying object path changes, not when the
  // signature is merely refreshed. (React's "adjust state during render" pattern.)
  // A model already held under another signature is reused as is.
  const [resolvedUrl, setResolvedUrl] = useState(() => heldUrls.get(stableKey(url)) ?? url)
  let effectiveUrl = resolvedUrl
  if (stableKey(url) !== stableKey(resolvedUrl)) {
    effectiveUrl = heldUrls.get(stableKey(url)) ?? url
    setResolvedUrl(effectiveUrl)
  }

  const gltf = useGLTF(effectiveUrl, true)

  useEffect(() => {
    // Re-subscribing: cancel any release scheduled for this url.
    const pending = pendingClears.get(effectiveUrl)
    if (pending !== undefined) {
      clearTimeout(pending)
      pendingClears.delete(effectiveUrl)
    }
    refCounts.set(effectiveUrl, (refCounts.get(effectiveUrl) ?? 0) + 1)
    const key = stableKey(effectiveUrl)
    if (!heldUrls.has(key)) heldUrls.set(key, effectiveUrl)

    return () => {
      const next = (refCounts.get(effectiveUrl) ?? 1) - 1
      refCounts.set(effectiveUrl, next)
      if (next > 0) return

      // Defer the GPU teardown. StrictMode runs this cleanup on every mount
      // (mount→unmount→mount); clearing synchronously would dispose the shared
      // scene/geometry that the immediately-remounting consumer — and the
      // clone it already built — still reference, blanking the model.
      const timer = setTimeout(() => {
        pendingClears.delete(effectiveUrl)
        if ((refCounts.get(effectiveUrl) ?? 0) <= 0) {
          refCounts.delete(effectiveUrl)
          if (heldUrls.get(key) === effectiveUrl) heldUrls.delete(key)
          useGLTF.clear(effectiveUrl)
        }
      }, CLEAR_DELAY_MS)
      pendingClears.set(effectiveUrl, timer)
    }
  }, [effectiveUrl])

  return gltf
}
